export const useSecurityQuestions = () => {
  const apiFetch = useSanctumClient();
  const securityQuestionsStore = useSecurityQuestionsStore();

  // Reactive state for settings
  const allowCustomQuestions = ref(false);
  const predefinedQuestions = ref([]);
  const minQuestions = ref(1);
  const maxQuestions = ref(1);

  /**
   * Check if user needs to set up security questions
   */
  const fetchRawStatus = () => {
    const userId = useSanctumUser<{ id?: unknown } | null>().value?.id;
    // Without a user id there is nothing safe to key on: no cache.
    if (userId == null) return apiFetch("/api/user/security-questions/status");
    return securityQuestionsStore.loadStatus(userId, () =>
      apiFetch("/api/user/security-questions/status"),
    );
  };

  const checkSecurityQuestionsStatus = async () => {
    try {
      const response = await fetchRawStatus();
      return {
        hasQuestions: response.has_questions,
        questionsCount: response.questions_count,
        needsSetup: response.should_prompt_setup,
      };
    }
    catch (error) {
      console.warn("Could not check security questions status:", error);
      return {
        hasQuestions: false,
        isMandatory: false,
        questionsCount: 0,
        needsSetup: false,
      };
    }
  };

  /**
   * Load predefined questions and settings
   */
  const loadPredefinedQuestions = async () => {
    try {
      const response = await apiFetch(
        "/api/user/security-questions/predefined",
      );
      predefinedQuestions.value = response.questions || [];
      allowCustomQuestions.value = response.allow_custom_questions || false;
      minQuestions.value = response.min_questions || 1;
      maxQuestions.value = response.max_questions || 1;
      return {
        questions: predefinedQuestions.value,
        allowCustom: allowCustomQuestions.value,
        minQuestions: minQuestions.value,
        maxQuestions: maxQuestions.value,
      };
    }
    catch (error) {
      console.warn("Could not load predefined questions:", error);
      return {
        questions: [],
        allowCustom: false,
        minQuestions: 1,
        maxQuestions: 1,
      };
    }
  };

  /**
   * Manually trigger security questions setup modal
   */
  const triggerSecurityQuestionsSetup = (intendedDestination?: string) => {
    console.log("Triggering security questions setup");
    securityQuestionsStore.setRequiredSetup(true, intendedDestination);
  };

  /**
   * Check and show setup modal if needed
   */
  const checkAndShowSetupIfNeeded = async (currentPath?: string) => {
    const status = await checkSecurityQuestionsStatus();
    if (status.needsSetup) {
      securityQuestionsStore.setRequiredSetup(true, currentPath);
      return true; // Setup was triggered
    }
    return false; // No setup needed
  };

  return {
    // State
    allowCustomQuestions: readonly(allowCustomQuestions),
    predefinedQuestions: readonly(predefinedQuestions),
    minQuestions: readonly(minQuestions),
    maxQuestions: readonly(maxQuestions),

    // Methods
    fetchRawStatus,
    checkSecurityQuestionsStatus,
    loadPredefinedQuestions,
    triggerSecurityQuestionsSetup,
    checkAndShowSetupIfNeeded,
  };
};
